import { memo, useCallback, useEffect } from "react"
import { Outlet, useLocation, useParams } from "react-router-dom"
import { AMapGL as Map } from "@wayz/react-gl"
import { ReactGLGeocoder, ReactGLNavigationController } from "@wayz/react-gl-plugins"
import { EventEmitter } from "fbemitter"
import { useCreation, useSetState } from "ahooks"
import classNames from "classnames"
// ==============================================
import FloatingControllerButton from "../FloatingControllerButton"
import IndustryPointUseAMapWithCluster from "../IndustryPoint"
// ==============================================
import { useControllerModel } from "@/models"
import MapContext from "./context"
import { MAP_SEARCH_URL } from "@/constants/config"
// ==============================================
import type { FC } from "react"
import type { MapGLScaleControlProps, ViewStateProps } from "@wayz/react-gl/lib/gl"
import type { IMapContext } from "./context"
import type { BranchShowVO, RouterIdsVO } from "@/api"
// ==============================================
import "./index.scss"
// ==============================================

interface stateVO {
  showLayer: boolean
  // 控制网点的显隐
  branchShow: BranchShowVO
}

const noop = () => {}

const scaleControl: MapGLScaleControlProps & {
  position: string
} = {
  show: true,
  unit: "metric",
  maxWidth: 100,
  position: "bottom-left",
}

const initialViewState = {
  pitch: 0,
  longitude: 120.16482,
  latitude: 30.243544,
  zoom: 7,
  maxZoom: 20,
  minZoom: 3,
  bearing: 0,
}

const MapContainer: FC<any> = ({
  // 是否显示浮层控制开关
  isShowSwitch = true,
  // 是否显示网点
  isShowPoint = true,
  // 是否显示搜索框
  isShowSearch = true,
  // 是否显示缩放控制
  isShowController = true,
}) => {
  const location = useLocation()
  const pathArr = location.pathname.split("/")
  const { theme } = useControllerModel()
  const params = useParams<RouterIdsVO>() as any
  const { cityId, brandId } = params
  const [state, setState] = useSetState<stateVO>({
    showLayer: false,
    branchShow: {
      unlockedShow: true,
      lockedShow: false,
      selectionShow: true,
    },
  })

  const { showLayer, branchShow } = state
  const searchParameters = {
    city: cityId?.slice(-6),
  }

  const emitter = useCreation(() => new EventEmitter(), [])

  const toggleIndustryPoint = useCallback(
    (isShow?: boolean) => {
      setState((prevState) => ({
        ...prevState,
        showLayer: !!isShow,
      }))
    },
    // eslint-disable-next-line
    [],
  )

  // 控制网点的显隐
  const toggleBranchShow = useCallback(
    (data: BranchShowVO) => {
      setState((prevState) => ({
        ...prevState,
        branchShow: {
          ...data,
        },
      }))
    },
    // eslint-disable-next-line
    [],
  )

  // 获取网点显隐情况
  const getBranchShow = useCallback(() => {
    return branchShow
  }, [branchShow])

  const value = useCreation<IMapContext>(
    () => ({
      toggleIndustryPoint,
      toggleBranchShow,
      getBranchShow,
      // @ts-expect-error
      getScale: noop,
      getViewState: noop,
      setViewState: noop,
      emitter,
    }),
    [toggleIndustryPoint, toggleBranchShow, getBranchShow, emitter],
  )

  // 监听viewState的变化
  const onViewStateChange = useCallback((viewState: ViewStateProps) => {
    emitter.emit("viewStateChange", viewState)
    // eslint-disable-next-line
  }, [])

  // 监听地图click事件
  const onMapClick = useCallback((info) => {
    emitter.emit("onMapClick", info)
    // eslint-disable-next-line
  }, [])

  // 监听地图hover事件
  const onMapHover = useCallback((info) => {
    emitter.emit("onMapHover", info)
    // eslint-disable-next-line
  }, [])

  const onMapError = useCallback((error) => {
    if (error?.message === "WebGL context is lost") {
      window.location.reload()
    }
  }, [])

  // 切换城市或品牌
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      branchShow: {
        unlockedShow: true,
        lockedShow: false,
        selectionShow: true,
      },
    }))
  }, [cityId, brandId, setState])

  return (
    <Map
      MAP_ACCESS_TOKEN={process.env.REACT_APP_AMAP_ACCESS_TOKEN!}
      initialViewState={initialViewState}
      onViewStateChange={onViewStateChange}
      mapStyle={(theme === "dark" ? "grey" : theme) as any}
      scaleControl={scaleControl}
      onClick={onMapClick}
      onHover={onMapHover}
      onError={onMapError}
    >
      <div className={classNames("map-container", `container-${pathArr[pathArr.length - 1]}`)}>
        {/* place search */}
        {isShowSearch && (
          <ReactGLGeocoder
            searchUrl={MAP_SEARCH_URL}
            searchParameters={searchParameters}
            placeholder="请输入关键字进行地点查询"
            showClose
            searchClassName="map-search-box"
          />
        )}
        {/* navigation controller */}
        {isShowController && <ReactGLNavigationController className="navigation-control" />}
        {/* 浮层控制开关 */}
        {isShowSwitch && <FloatingControllerButton params={params} />}
        <MapContext.Provider value={value}>
          {/* 网点 */}
          {isShowPoint && (
            <IndustryPointUseAMapWithCluster visible={showLayer} branchVisible={branchShow} />
          )}
          {/** 子路由 */}
          <Outlet />
        </MapContext.Provider>
      </div>
    </Map>
  )
}

export default memo(MapContainer)
